/**
 * DataSource: Planos de Ação (GUPM-187).
 * --------------------------------------
 * Choke point de `planos_acao` — a UI nunca chama `supabase.from()` (regra #2).
 * POC: Supabase direto. Audit via trigger genérico `audit_planos_acao`. RLS:
 * leitura autenticada; escrita só RT/admin (operador read-only, ADR-0009).
 * Broadcast próprio (`planos:change`, C-24) ao criar plano e mudar ação — fora do
 * canal `tarp:critical`, que é contrato de `tarp_records`.
 */
import { supabase } from '@/lib/supabase';
import { emitPlanoAlterado } from './planos-realtime';
import {
  derivePlanoStatus,
  isPlanoEditavel,
  type AcaoPlano,
  type AcaoStatus,
  type Plano,
  type PlanoStatus,
} from '@/domain/planos';
import type { DataSourceMeta } from './types';

export interface CreatePlanoInput {
  titulo: string;
  tarpRecordId?: string | null;
  damId?: string | null;
  tarpCode?: string | null;
  nivel?: string | null;
  modo?: string | null;
  /** Instrumento/componente associado (contexto na lista de ações — GUPM-193). */
  instrumento?: string | null;
  /** Local da ação (GUPM-193): descrição + UTM 23S SIRGAS2000 (map picker do M2). */
  localDesc?: string | null;
  localUtmE?: number | null;
  localUtmN?: number | null;
  acoes: AcaoPlano[];
}

export interface PlanosDataSource extends DataSourceMeta {
  listPlanos(): Promise<Plano[]>;
  getPlano(id: string): Promise<Plano | null>;
  /** Planos vinculados a uma TARP (por `tarp_record_id`) — usado na baixa (GUPM-213). */
  listPlanosByTarp(tarpRecordId: string): Promise<Plano[]>;
  createPlano(input: CreatePlanoInput): Promise<Plano>;
  /** Atualiza o status de UMA ação (read-modify-write do jsonb) e re-deriva o status do plano. */
  updateAcaoStatus(planoId: string, acaoId: string, status: AcaoStatus): Promise<Plano>;
}

type Embedded = { code: string } | { code: string }[] | null;

interface PlanoRow {
  id: string;
  codigo: string;
  tarp_record_id: string | null;
  dam_id: string | null;
  tarp_code: string | null;
  titulo: string;
  nivel: string | null;
  modo: string | null;
  instrumento: string | null;
  status: PlanoStatus;
  acoes: AcaoPlano[] | null;
  created_at: string;
  updated_at: string | null;
  dam?: Embedded;
  tarp?: EmbeddedTarp;
}

type EmbeddedTarp = { seq: number } | { seq: number }[] | null;

const SELECT =
  'id, codigo, tarp_record_id, dam_id, tarp_code, titulo, nivel, modo, instrumento, status, acoes, ' +
  'created_at, updated_at, dam:dams!planos_acao_dam_id_fkey(code), ' +
  'tarp:tarp_records!planos_acao_tarp_record_id_fkey(seq)';

function unwrapCode(raw: Embedded | undefined): string {
  const one = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
  return one ? one.code : '';
}

function unwrapSeq(raw: EmbeddedTarp | undefined): number | null {
  const one = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
  return one ? one.seq : null;
}

function mapRow(r: PlanoRow): Plano {
  return {
    id: r.id,
    codigo: r.codigo,
    tarpRecordId: r.tarp_record_id,
    tarpSeq: unwrapSeq(r.tarp),
    damId: r.dam_id,
    damCode: unwrapCode(r.dam),
    tarpCode: r.tarp_code,
    titulo: r.titulo,
    nivel: r.nivel,
    modo: r.modo,
    instrumento: r.instrumento,
    status: r.status,
    acoes: Array.isArray(r.acoes) ? r.acoes : [],
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

async function currentAppUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const filter = user.email
    ? `auth_user_id.eq.${user.id},email.eq.${user.email}`
    : `auth_user_id.eq.${user.id}`;
  const { data, error } = await supabase
    .from('app_users_public')
    .select('id')
    .or(filter)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.id ?? null;
}

export const manualPlanos: PlanosDataSource = {
  mode: 'manual',

  async listPlanos() {
    const { data, error } = await supabase
      .from('planos_acao')
      .select(SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(200)
      .returns<PlanoRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getPlano(id) {
    const { data, error } = await supabase
      .from('planos_acao')
      .select(SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()
      .returns<PlanoRow | null>();
    if (error) throw error;
    return data ? mapRow(data) : null;
  },

  async listPlanosByTarp(tarpRecordId) {
    const { data, error } = await supabase
      .from('planos_acao')
      .select(SELECT)
      .eq('tarp_record_id', tarpRecordId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .returns<PlanoRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async createPlano(input) {
    const createdBy = await currentAppUserId();
    const status = derivePlanoStatus(input.acoes);
    // `codigo` é omitido de propósito: o DEFAULT (sequence PA-xxxxx) preenche no DB
    // sem risco de colisão (finding #104 — substitui o antigo gerarCodigo por Date.now).
    const { data, error } = await supabase
      .from('planos_acao')
      .insert({
        tarp_record_id: input.tarpRecordId ?? null,
        dam_id: input.damId ?? null,
        tarp_code: input.tarpCode ?? null,
        titulo: input.titulo,
        nivel: input.nivel ?? null,
        modo: input.modo ?? null,
        instrumento: input.instrumento ?? null,
        local_desc: input.localDesc ?? null,
        local_utm_e: input.localUtmE ?? null,
        local_utm_n: input.localUtmN ?? null,
        status,
        acoes: input.acoes,
        created_by: createdBy,
      })
      .select(SELECT)
      .single()
      .returns<PlanoRow>();
    if (error) throw error;
    if (!data) throw new Error('createPlano não retornou row');
    emitPlanoAlterado({ event: 'create', id: data.id });
    return mapRow(data);
  },

  async updateAcaoStatus(planoId, acaoId, status) {
    // Read-modify-write do jsonb: lê as ações, muda a alvo, re-deriva o status do
    // plano e persiste. Guard otimista (finding #104): a escrita casa o `updated_at`
    // lido → se outra pessoa alterou o plano nesse meio-tempo, 0 linhas voltam e
    // abortamos em vez de sobrescrever silenciosamente (lost update).
    const atual = await manualPlanos.getPlano(planoId);
    if (!atual) throw new Error('updateAcaoStatus: plano inexistente');
    if (!isPlanoEditavel(atual.status)) throw new Error('Plano extinto não pode ser alterado.');
    const acoes = atual.acoes.map((a) => (a.id === acaoId ? { ...a, status } : a));
    const novoStatus = derivePlanoStatus(acoes);

    const base = supabase.from('planos_acao').update({ acoes, status: novoStatus }).eq('id', planoId);
    // Um plano recém-criado tem updated_at = null (set_updated_at só dispara em UPDATE).
    const guarded = atual.updatedAt == null ? base.is('updated_at', null) : base.eq('updated_at', atual.updatedAt);
    const { data, error } = await guarded.select(SELECT).maybeSingle().returns<PlanoRow | null>();
    if (error) throw error;
    if (!data) {
      throw new Error('Conflito: o plano foi alterado por outra pessoa. Recarregue e tente novamente.');
    }
    emitPlanoAlterado({ event: 'update', id: data.id });
    return mapRow(data);
  },
};
